const CORTADO_FORM_MESSAGE_TYPE = 'cortado-form'
const CORTADO_EMBED_MESSAGE_TYPE = 'cortado-embed'

type MessagePayload =
  | { action: 'resize'; height: number }
  | { action: 'ready' }
  | { action: 'step-change'; step: number; totalSteps: number }
  | { action: 'submit-success'; redirectUrl?: string }
  | { action: 'error'; message: string }
  | {
      action: 'conversion'
      googleTagId?: string
      googleAdsConversionLabel: string
      eventType?: string
      value?: number
      currency?: string
    }

export interface CortadoFormMessage {
  type: typeof CORTADO_FORM_MESSAGE_TYPE
  payload: MessagePayload
}

/**
 * Attribution data received from the parent window via postMessage.
 * Keys are a strict allowlist of UTM parameters, click IDs, and page context.
 */
export type AttributionData = Record<string, string>

const ATTRIBUTION_ALLOWLIST = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'referrer_url',
  'landing_page_url',
])

let storedAttribution: AttributionData | null = null
let hostConversionHandlerReady = false

/**
 * Get the stored attribution data (if any).
 * Returns null if no attribution was received.
 */
export function getAttributionData(): AttributionData | null {
  return storedAttribution
}

/**
 * Whether the embedding host has explicitly confirmed it can fire Ads
 * conversions from the top-level page.
 */
export function canForwardConversionToHost(): boolean {
  return hostConversionHandlerReady
}

/**
 * Capture attribution from the form's own URL query string and document.referrer.
 * Used when the form is opened directly (e.g. an Instagram bio link or a
 * Google Ads landing URL) without an embedding parent that posts attribution.
 *
 * Seeds `storedAttribution` only if it hasn't been set yet — a parent's
 * postMessage (delivered later) is allowed to overwrite this baseline.
 */
export function captureUrlAttribution(): void {
  if (storedAttribution) return

  const captured: AttributionData = {}

  try {
    const params = new URLSearchParams(window.location.search)
    for (const [key, value] of params.entries()) {
      if (ATTRIBUTION_ALLOWLIST.has(key) && value) {
        captured[key] = value
      }
    }
  } catch {
    // Malformed URL — fall through to referrer fallback
  }

  if (!captured.referrer_url && document.referrer) {
    captured.referrer_url = document.referrer
  }

  if (Object.keys(captured).length > 0) {
    storedAttribution = captured
  }
}

/**
 * Initialize the parent message listener.
 * Listens for attribution and capability messages from the parent window.
 * Origin is not validated because forms are embedded on arbitrary customer
 * domains.
 */
export function initAttributionListener(): void {
  window.addEventListener('message', (event: MessageEvent) => {
    if (!event.data) return

    if (
      event.data.type === CORTADO_EMBED_MESSAGE_TYPE &&
      event.data.payload?.action === 'host-conversion-ready'
    ) {
      hostConversionHandlerReady = true
      return
    }

    if (event.data.type !== 'attribution') return

    const raw = event.data.data
    if (!raw || typeof raw !== 'object') return

    const filtered: AttributionData = {}
    for (const [key, value] of Object.entries(raw)) {
      if (ATTRIBUTION_ALLOWLIST.has(key) && typeof value === 'string' && value) {
        filtered[key] = value
      }
    }

    if (Object.keys(filtered).length > 0) {
      storedAttribution = filtered
    }
  })
}

/**
 * Send a message to the parent window (if in iframe context)
 */
export function postToParent(payload: MessagePayload): void {
  // Only send if in iframe context
  if (!isInIframe()) return

  const message: CortadoFormMessage = {
    type: CORTADO_FORM_MESSAGE_TYPE,
    payload,
  }

  window.parent.postMessage(message, '*')
}

/**
 * Check if the current window is running inside an iframe
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch {
    // Blocked by CORS means we're in iframe
    return true
  }
}

/**
 * Send a resize message to parent
 */
export function sendResizeMessage(height: number): void {
  postToParent({ action: 'resize', height })
}

/**
 * Send a ready message to parent
 */
export function sendReadyMessage(): void {
  postToParent({ action: 'ready' })
}

/**
 * Send a step change message to parent
 */
export function sendStepChangeMessage(step: number, totalSteps: number): void {
  postToParent({ action: 'step-change', step, totalSteps })
}

/**
 * Send a submit success message to parent.
 * If `redirectUrl` is provided, the parent may navigate to it; the parent decides
 * whether to honor the URL.
 */
export function sendSubmitSuccessMessage(redirectUrl?: string): void {
  postToParent({ action: 'submit-success', ...(redirectUrl ? { redirectUrl } : {}) })
}

/**
 * Send an error message to parent
 */
export function sendErrorMessage(message: string): void {
  postToParent({ action: 'error', message })
}

/**
 * Forward a Google Ads conversion event to the host page.
 *
 * Ad attribution requires firing on the host's top-level domain so the
 * conversion is bound to the gclid cookie set when the visitor clicked the
 * ad. The host's `embed.js` listens for this message and calls gtag in
 * the host window — see `form/public/embed.js`.
 */
export function sendConversionMessage(args: {
  googleTagId?: string
  googleAdsConversionLabel: string
  eventType?: string
  value?: number
  currency?: string
}): void {
  postToParent({
    action: 'conversion',
    googleTagId: args.googleTagId,
    googleAdsConversionLabel: args.googleAdsConversionLabel,
    eventType: args.eventType,
    value: args.value,
    currency: args.currency,
  })
}
